import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";

export interface PartnerBranding {
  partnerId: number;
  nome: string;
  corPrimaria: string | null;
  corSecundaria: string | null;
  corLeve: string | null;
  logoUrl: string | null;
  logoIndicacao: string | null;
  dominio: string | null;
  commissionRateRelatorio: number;
  parentPartnerId: number | null;
  status: string;
  sidebarMode: "white" | "custom";
  sidebarBgColor: string | null;
  sidebarActiveColor: string | null;
}

export interface SubPartnerInfo {
  id: number;
  nome: string;
  logoIndicacao: string | null;
}

export function usePartnerBranding() {
  const { tenantId, user } = useAuth();

  // Fetch partner info for current organization
  const { data: partnerData, isLoading: isLoadingPartner } = useQuery({
    queryKey: ["partner-branding", tenantId],
    queryFn: async () => {
      if (!tenantId) {
        console.log("[PartnerBranding] No tenantId available");
        return null;
      }

      const orgId = parseInt(tenantId, 10);
      console.log("[PartnerBranding] Fetching for org:", orgId);

      // First check if this organization IS a partner
      const { data: asPartner, error: partnerError } = await supabase
        .from("partners")
        .select("*")
        .eq("organization_id", orgId)
        .eq("status", "ativo")
        .maybeSingle();

      console.log("[PartnerBranding] Is org a partner?", asPartner, partnerError);

      if (asPartner) {
        // This org is a partner - use its own branding
        return {
          isPartner: true,
          branding: {
            partnerId: asPartner.id,
            nome: asPartner.nome || "",
            corPrimaria: asPartner.cor_primaria,
            corSecundaria: asPartner.cor_secondaria,
            corLeve: asPartner.cor_leve,
            logoUrl: asPartner.logo_url,
            logoIndicacao: asPartner.logo_indicacao,
            dominio: asPartner.dominio,
            commissionRateRelatorio: Number(asPartner.commission_rate_relatorio) || 0,
            parentPartnerId: asPartner.parent_partner_id,
            status: asPartner.status,
            sidebarMode: (asPartner as any).sidebar_mode || "white",
            sidebarBgColor: (asPartner as any).sidebar_bg_color,
            sidebarActiveColor: (asPartner as any).sidebar_active_color,
          } as PartnerBranding,
          subPartner: null as SubPartnerInfo | null,
        };
      }

      // Check if this organization is associated with a partner
      const { data: org, error: orgError } = await supabase
        .from("organizations")
        .select("partner_id")
        .eq("id", orgId)
        .maybeSingle();

      console.log("[PartnerBranding] Org data:", org, "error:", orgError);

      if (!org?.partner_id) {
        console.log("[PartnerBranding] No partner_id found for org");
        return null;
      }

      // Fetch the partner details
      const { data: partner, error: fetchError } = await supabase
        .from("partners")
        .select("*")
        .eq("id", org.partner_id)
        .eq("status", "ativo")
        .maybeSingle();

      console.log("[PartnerBranding] Partner data:", partner, "error:", fetchError);

      if (!partner) return null;

      // If partner has a parent (is subpartner), fetch parent for branding
      let mainPartner = partner;
      let subPartner: SubPartnerInfo | null = null;

      if (partner.parent_partner_id) {
        const { data: parentPartner } = await supabase
          .from("partners")
          .select("*")
          .eq("id", partner.parent_partner_id)
          .eq("status", "ativo")
          .maybeSingle();

        if (parentPartner) {
          subPartner = {
            id: partner.id,
            nome: partner.nome || "",
            logoIndicacao: partner.logo_indicacao,
          };
          mainPartner = parentPartner;
        }
      }

      return {
        isPartner: false,
        branding: {
          partnerId: mainPartner.id,
          nome: mainPartner.nome || "",
          corPrimaria: mainPartner.cor_primaria,
          corSecundaria: mainPartner.cor_secondaria,
          corLeve: mainPartner.cor_leve,
          logoUrl: mainPartner.logo_url,
          logoIndicacao: mainPartner.logo_indicacao,
          dominio: mainPartner.dominio,
          commissionRateRelatorio: Number(mainPartner.commission_rate_relatorio) || 0,
          parentPartnerId: mainPartner.parent_partner_id,
          status: mainPartner.status,
          sidebarMode: (mainPartner as any).sidebar_mode || "white",
          sidebarBgColor: (mainPartner as any).sidebar_bg_color,
          sidebarActiveColor: (mainPartner as any).sidebar_active_color,
        } as PartnerBranding,
        subPartner,
      };
    },
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Apply CSS custom properties when branding changes
  // This overrides the design system's --primary and --accent colors so all buttons/elements use partner color
  useEffect(() => {
    if (partnerData?.branding) {
      const { corPrimaria, corSecundaria, corLeve } = partnerData.branding;
      const root = document.documentElement;

      // Convert hex to HSL for Tailwind compatibility
      const hexToHsl = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        
        let r = parseInt(result[1], 16) / 255;
        let g = parseInt(result[2], 16) / 255;
        let b = parseInt(result[3], 16) / 255;
        
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0, s = 0;
        const l = (max + min) / 2;
        
        if (max !== min) {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
          }
        }
        
        return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
      };

      // Create a lighter version for accent (background on hover)
      const hexToLightHsl = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        
        let r = parseInt(result[1], 16) / 255;
        let g = parseInt(result[2], 16) / 255;
        let b = parseInt(result[3], 16) / 255;
        
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0, s = 0;
        const l = (max + min) / 2;
        
        if (max !== min) {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
          }
        }
        
        // Make it lighter for accent backgrounds (95% lightness)
        return `${Math.round(h * 360)} ${Math.round(s * 100)}% 95%`;
      };

      if (corPrimaria) {
        const primaryHsl = hexToHsl(corPrimaria);
        const accentHsl = hexToLightHsl(corPrimaria);
        root.style.setProperty("--primary", primaryHsl);
        root.style.setProperty("--accent", accentHsl);
        root.style.setProperty("--ring", primaryHsl);
        root.style.setProperty("--partner-primary", corPrimaria);
      }
      if (corSecundaria) {
        root.style.setProperty("--partner-secondary", corSecundaria);
      }
      if (corLeve) {
        root.style.setProperty("--partner-light", corLeve);
      }

      return () => {
        root.style.removeProperty("--primary");
        root.style.removeProperty("--accent");
        root.style.removeProperty("--ring");
        root.style.removeProperty("--partner-primary");
        root.style.removeProperty("--partner-secondary");
        root.style.removeProperty("--partner-light");
      };
    }
  }, [partnerData?.branding]);

  return {
    partnerId: partnerData?.branding?.partnerId ?? null,
    branding: partnerData?.branding ?? null,
    subPartner: partnerData?.subPartner ?? null,
    isPartner: partnerData?.isPartner ?? false,
    isLoading: isLoadingPartner,
    hasPartnerBranding: !!partnerData?.branding,
  };
}
